import { createRouter, createRootRoute, createRoute, redirect } from "@tanstack/react-router";
import { LoginPage } from "@/pages/login";
import { RootLayout, AppLayout, RequireSession } from "@/pages/__root";
import { OrdersPage } from "@/pages/orders/index";
import { OrderDetailPage } from "@/pages/orders/$orderId";
import { NewOrderPage } from "@/pages/orders/new";
import { NewStorePage } from "@/pages/stores/new";
import { EditStorePage } from "@/pages/stores/$storeId.edit";
import { SettingsPage } from "@/pages/settings";
import { DashboardPage } from "@/pages/dashboard";
import { GodViewPage } from "@/pages/god-view";
import { ScanPage } from "@/pages/warehouse/scan";
import { StockCheckPage } from "@/pages/stock-check";
import { LocationsPage } from "@/pages/warehouse/locations";
import { PickPage } from "@/pages/fulfillment/pick";
import { PackPage } from "@/pages/fulfillment/pack";
import { CodPage } from "@/pages/fulfillment/cod";
import { LabelsPage } from "@/pages/fulfillment/labels";
import { ReturnedPackagesPage } from "@/pages/fulfillment/returned";
import { MergeShipmentsPage } from "@/pages/fulfillment/merge";
import { MovePage } from "@/pages/warehouse/move";
import { BannedCustomersPage } from "@/pages/banned-customers/index";
import { BugReportsPage } from "@/pages/bug-reports/index";
import { CustomersPage } from "@/pages/customers/index";
import { CouponsPage } from "@/pages/coupons/index";
import { SkuGenPage } from "@/pages/skugen";
import { ProductsPage } from "@/pages/products/index";
import { NewProductPage } from "@/pages/products/new";
import { EditProductPage } from "@/pages/products/$productId";
import { CountingListPage } from "@/pages/warehouse/counting/index";
import { CountingDetailPage } from "@/pages/warehouse/counting/$invoiceId";
import { CountingCounterPage } from "@/pages/warehouse/counting/$invoiceId.count";
import { ShipmentSessionsPage } from "@/pages/warehouse/shipments/index";
import { ShipmentSessionDetailPage } from "@/pages/warehouse/shipments/$sessionId";
import { ShipmentCountPage } from "@/pages/warehouse/shipments/$sessionId.count";
import { AnalyticsPage } from "@/pages/analytics";
import { ReturnsPage } from "@/pages/returns/index";
import { ReturnsAnalyticsPage } from "@/pages/returns/analitika";
import { ReturnDetailPage } from "@/pages/returns/$returnId";
import { PublicReturnFormPage } from "@/pages/return/$slug";
import { PublicReturnStatusPage } from "@/pages/return/$slug.status.$ref";
import { OrderSharePage } from "@/pages/public/order-share";
import { WholesaleSharePage } from "@/pages/public/wholesale-share";
import { AdminOnly } from "@/components/auth/admin-only";
import { canManageBugReports } from "@/lib/roles";
import { WholesaleOnly } from "@/components/auth/wholesale-only";
import { WholesaleOrdersPage } from "@/pages/wholesale/orders/index";
import { NewWholesaleOrderPage } from "@/pages/wholesale/orders/new";
import { WholesaleOrderDetailPage } from "@/pages/wholesale/orders/$orderId";
import { WholesalePartnersPage } from "@/pages/wholesale/partners/index";
import { WholesalePartnerDetailPage } from "@/pages/wholesale/partners/$partnerId";
import { WholesaleCatalogPage } from "@/pages/wholesale/catalog";
import { WholesaleAnalyticsPage } from "@/pages/wholesale/analytics";
import { WholesaleShowroomPage } from "@/pages/wholesale/showroom";
import { WholesaleSettingsPage } from "@/pages/wholesale/settings";
import { createElement } from "react";

const adminWrap = (Comp: React.ComponentType) => () =>
  createElement(AdminOnly, null, createElement(Comp));

const bugReportsWrap = (Comp: React.ComponentType) => () =>
  createElement(AdminOnly, { allow: canManageBugReports, children: createElement(Comp) });

const wholesaleWrap = (Comp: React.ComponentType) => () =>
  createElement(WholesaleOnly, null, createElement(Comp));

const rootRoute = createRootRoute({
  component: RootLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_app",
  component: AppLayout,
});

const ordersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orders",
  component: OrdersPage,
  // ?search= lets the ⌘K palette hand a query off to the full orders list.
  // Return type is explicit so `search` stays optional — otherwise every existing
  // <Link to="/orders"> would be required to pass it.
  validateSearch: (search: Record<string, unknown>): { search?: string } =>
    typeof search.search === "string" && search.search ? { search: search.search } : {},
});

export type NewOrderSearch = { returnId?: string };

/** `?returnId=` prebacuje formu u „return mode" — prefill iz zahtjeva za zamjenu.
 *  Povratni tip je eksplicitan da `returnId` ostane opcionalan, pa postojeći
 *  <Link to="/orders/new"> ne mora ništa prosljeđivati. */
export const newOrderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orders/new",
  component: NewOrderPage,
  validateSearch: (search: Record<string, unknown>): NewOrderSearch =>
    typeof search.returnId === "string" && search.returnId ? { returnId: search.returnId } : {},
});

const orderDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orders/$orderId",
  component: OrderDetailPage,
});

const storesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/stores",
  beforeLoad: () => {
    throw redirect({ to: "/settings" });
  },
});

const storesNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/stores/new",
  component: NewStorePage,
});

const storeEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/stores/$storeId/edit",
  component: EditStorePage,
});

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users",
  beforeLoad: () => {
    throw redirect({ to: "/settings" });
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dashboard",
  component: adminWrap(DashboardPage),
});

const analyticsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/analytics",
  component: adminWrap(AnalyticsPage),
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  component: SettingsPage,
});

const godViewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/god-view",
  component: adminWrap(GodViewPage),
});

const stockCheckRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/provjera-stanja",
  component: StockCheckPage,
});

const warehouseScanRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/warehouse/scan",
  component: ScanPage,
});

const warehouseLocationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/warehouse/locations",
  component: LocationsPage,
});

const countingListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/warehouse/brojanje-robe",
  component: CountingListPage,
});

const countingDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/warehouse/brojanje-robe/$invoiceId",
  component: CountingDetailPage,
});

const countingCounterRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/warehouse/brojanje-robe/$invoiceId/count",
  component: CountingCounterPage,
});

const shipmentSessionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/warehouse/brojanje-posiljki",
  component: ShipmentSessionsPage,
});

const shipmentSessionDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/warehouse/brojanje-posiljki/$sessionId",
  component: ShipmentSessionDetailPage,
});

const shipmentCountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/warehouse/brojanje-posiljki/$sessionId/count",
  component: ShipmentCountPage,
});

const fulfillmentPickRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/fulfillment/pick",
  component: PickPage,
});

const fulfillmentPackRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/fulfillment/pack",
  component: PackPage,
});

const fulfillmentCodRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/fulfillment/cod",
  component: CodPage,
});

export type ReturnedPackagesSearch = { q?: string };

const fulfillmentReturnedRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/fulfillment/returned",
  component: ReturnedPackagesPage,
  validateSearch: (search: Record<string, unknown>): ReturnedPackagesSearch => ({
    q: typeof search.q === "string" ? search.q : "",
  }),
});

/** `group` = id grupe kandidata, `meta` = id ili broj meta narudžbe ("12" / "M-12"): skrol i istaknuta kartica. */
export type MergeShipmentsSearch = { group?: string; meta?: string };

const searchString = (v: unknown): string | undefined =>
  typeof v === "string" && v ? v : typeof v === "number" ? String(v) : undefined;

const fulfillmentMergeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/fulfillment/merge",
  component: MergeShipmentsPage,
  validateSearch: (search: Record<string, unknown>): MergeShipmentsSearch => ({
    group: searchString(search.group),
    meta: searchString(search.meta),
  }),
});

const fulfillmentLabelsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/fulfillment/labels",
  component: LabelsPage,
});

const warehouseMoveRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/warehouse/move",
  component: MovePage,
});

const customersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/customers",
  component: CustomersPage,
});

const couponsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/coupons",
  component: CouponsPage,
});

const bannedCustomersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/banned-customers",
  component: BannedCustomersPage,
});

const bugReportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/bugovi",
  component: bugReportsWrap(BugReportsPage),
});

const skugenRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/skugen",
  component: SkuGenPage,
});

const productsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/products",
  component: ProductsPage,
});

const newProductRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/products/new",
  component: NewProductPage,
});

const editProductRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/products/$productId",
  component: EditProductPage,
});

const wholesaleOrdersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wholesale/orders",
  component: wholesaleWrap(WholesaleOrdersPage),
});

const wholesaleNewOrderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wholesale/orders/new",
  component: wholesaleWrap(NewWholesaleOrderPage),
});

const wholesaleOrderDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wholesale/orders/$orderId",
  component: wholesaleWrap(WholesaleOrderDetailPage),
});

const wholesalePartnersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wholesale/partners",
  component: wholesaleWrap(WholesalePartnersPage),
});

const wholesalePartnerDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wholesale/partners/$partnerId",
  component: wholesaleWrap(WholesalePartnerDetailPage),
});

const wholesaleAnalyticsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wholesale/analytics",
  component: wholesaleWrap(WholesaleAnalyticsPage),
});

const wholesaleCatalogRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wholesale/catalog",
  component: wholesaleWrap(WholesaleCatalogPage),
});

/** Prezentacijski katalog ide pod `rootRoute`, ne pod `appRoute`: sidebar, mobilni header
 *  i `max-w-5xl` omotač `AppLayout`-a bi pojeli prostor koji je ovdje cijela poenta.
 *  Sesiju zato čuva `RequireSession` direktno. */
const wholesaleShowroomRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/wholesale/prezentacija",
  component: () =>
    createElement(RequireSession, null, createElement(WholesaleOnly, null, createElement(WholesaleShowroomPage))),
});

const wholesaleSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wholesale/settings",
  component: wholesaleWrap(adminWrap(WholesaleSettingsPage)),
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard" });
  },
});

export type ReturnsSearch = {
  status?: string;
  type?: string;
  q?: string;
  from?: string;
  to?: string;
  page?: number;
  stale?: boolean;
};

export const returnsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/returns",
  component: ReturnsPage,
  validateSearch: (search: Record<string, unknown>): ReturnsSearch => ({
    status: typeof search.status === "string" ? search.status : "all",
    type: typeof search.type === "string" ? search.type : "all",
    q: typeof search.q === "string" ? search.q : "",
    from: typeof search.from === "string" ? search.from : "",
    to: typeof search.to === "string" ? search.to : "",
    page: Number(search.page) > 0 ? Number(search.page) : 1,
    stale: search.stale === true || search.stale === "true" || search.stale === "1",
  }),
});

export type ReturnsAnalyticsSearch = { days?: number };

/** Statička putanja mora biti registrovana prije `/returns/$returnId` da je router
 *  ne bi pročitao kao id zahtjeva. */
export const returnsAnalyticsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/returns/analitika",
  component: ReturnsAnalyticsPage,
  validateSearch: (search: Record<string, unknown>): ReturnsAnalyticsSearch => {
    const days = Number(search.days);
    return { days: [7, 30, 90].includes(days) ? days : 30 };
  },
});

const returnDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/returns/$returnId",
  component: ReturnDetailPage,
});

const publicReturnFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/return/$slug",
  component: PublicReturnFormPage,
});

const publicReturnStatusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/return/$slug/status/$ref",
  component: PublicReturnStatusPage,
});

const publicOrderShareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/s/$token",
  component: OrderSharePage,
});

/** Javni predračun veleprodaje; OG meta tagove za ovaj URL ubacuje `routes/public-wholesale-page.ts`. */
const publicWholesaleShareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/vp/$token",
  component: WholesaleSharePage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  publicReturnFormRoute,
  publicReturnStatusRoute,
  publicOrderShareRoute,
  publicWholesaleShareRoute,
  wholesaleShowroomRoute,
  appRoute.addChildren([
    indexRoute,
    dashboardRoute,
    analyticsRoute,
    ordersRoute,
    newOrderRoute,
    orderDetailRoute,
    storesRoute,
    storesNewRoute,
    storeEditRoute,
    usersRoute,
    godViewRoute,
    stockCheckRoute,
    warehouseScanRoute,
    warehouseLocationsRoute,
    countingListRoute,
    countingDetailRoute,
    countingCounterRoute,
    shipmentSessionsRoute,
    shipmentSessionDetailRoute,
    shipmentCountRoute,
    fulfillmentPickRoute,
    fulfillmentPackRoute,
    fulfillmentCodRoute,
    fulfillmentReturnedRoute,
    fulfillmentMergeRoute,
    fulfillmentLabelsRoute,
    warehouseMoveRoute,
    customersRoute,
    couponsRoute,
    bannedCustomersRoute,
    bugReportsRoute,
    returnsRoute,
    returnsAnalyticsRoute,
    returnDetailRoute,
    skugenRoute,
    productsRoute,
    newProductRoute,
    editProductRoute,
    settingsRoute,
    wholesaleOrdersRoute,
    wholesaleNewOrderRoute,
    wholesaleOrderDetailRoute,
    wholesalePartnersRoute,
    wholesalePartnerDetailRoute,
    wholesaleCatalogRoute,
    wholesaleAnalyticsRoute,
    wholesaleSettingsRoute,
  ]),
]);

export const router = createRouter({ routeTree });

export {
  rootRoute,
  loginRoute,
  appRoute,
  dashboardRoute,
  analyticsRoute,
  ordersRoute,
  orderDetailRoute,
  storesRoute,
  storesNewRoute,
  storeEditRoute,
  usersRoute,
  godViewRoute,
  warehouseScanRoute,
  warehouseLocationsRoute,
  fulfillmentPickRoute,
  fulfillmentPackRoute,
  fulfillmentCodRoute,
  fulfillmentReturnedRoute,
  fulfillmentMergeRoute,
  fulfillmentLabelsRoute,
  warehouseMoveRoute,
  customersRoute,
  couponsRoute,
  bannedCustomersRoute,
  bugReportsRoute,
  skugenRoute,
  productsRoute,
  newProductRoute,
  editProductRoute,
  countingListRoute,
  countingDetailRoute,
  countingCounterRoute,
  shipmentSessionsRoute,
  shipmentSessionDetailRoute,
  shipmentCountRoute,
  settingsRoute,
  returnDetailRoute,
  publicReturnFormRoute,
  publicReturnStatusRoute,
  publicOrderShareRoute,
  publicWholesaleShareRoute,
  wholesaleOrdersRoute,
  wholesaleNewOrderRoute,
  wholesaleOrderDetailRoute,
  wholesalePartnersRoute,
  wholesalePartnerDetailRoute,
  wholesaleCatalogRoute,
  wholesaleAnalyticsRoute,
  wholesaleShowroomRoute,
  wholesaleSettingsRoute,
};

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
